/**
 * offlineDB.js — Dexie.js IndexedDB layer for Offline-First Sync
 *
 * Stores:
 *   mutationQueue – outbox for POST/PUT/DELETE requests made while offline
 *   authStore     – JWT token accessible by the Service Worker (SW can't read localStorage)
 *   fields        – cached GET data for offline viewing
 *   crops         – cached GET data for offline viewing
 *   recommendations – cached GET data for offline viewing
 */
import Dexie from 'dexie';
import { v4 as uuidv4 } from 'uuid';

// ─── Database Definition ───────────────────────────────────────────
const db = new Dexie('obfs-offline');

db.version(1).stores({
  mutationQueue:   '++id, clientId, url, method, timestamp, status',
  authStore:       'key',          // single-row key-value
  fields:          'cacheKey',     // cacheKey = URL path
  crops:           'cacheKey',
  recommendations: 'cacheKey',
});

// ─── Auth Token Helpers ────────────────────────────────────────────

/**
 * Persist the JWT so the Service Worker can attach Authorization headers.
 * Called from the axios request interceptor every time a request is made.
 */
export const saveAuthToken = async (token) => {
  if (!token) return;
  try {
    await db.authStore.put({ key: 'jwt', token });
  } catch (err) {
    console.error('[OfflineDB] saveAuthToken failed:', err);
  }
};

export const getAuthToken = async () => {
  try {
    const row = await db.authStore.get('jwt');
    return row?.token || null;
  } catch (err) {
    console.error('[OfflineDB] getAuthToken failed:', err);
    return null;
  }
};

// ─── FormData / Blob Extraction ────────────────────────────────────

/**
 * If the payload is FormData, we can't JSON.stringify it. Instead we:
 *   1. Pull out every File / Blob entry → store in `blobs` array
 *   2. Keep the remaining text fields in a plain `jsonPayload` object
 * On replay the Service Worker reconstructs FormData from these two pieces.
 */
const extractFormData = (payload) => {
  if (!(payload instanceof FormData)) {
    return { jsonPayload: payload, blobs: null, isFormData: false };
  }

  const jsonPayload = {};
  const blobs = [];

  for (const [key, value] of payload.entries()) {
    if (value instanceof File || value instanceof Blob) {
      blobs.push({
        fieldName: key,
        blob: value,                       // Dexie can store raw Blobs
        filename: value.name || 'file',
        type: value.type || 'application/octet-stream',
      });
    } else {
      jsonPayload[key] = value;
    }
  }

  return { jsonPayload, blobs: blobs.length > 0 ? blobs : null, isFormData: true };
};

// ─── Mutation Queue (Outbox) ───────────────────────────────────────

/**
 * Queue a failed / offline request for later replay.
 * Returns the generated clientId so the UI can use it for optimistic updates.
 */
export const addToQueue = async (url, method, payload) => {
  const clientId = uuidv4();
  const { jsonPayload, blobs, isFormData } = extractFormData(payload);

  // Inject clientId into the payload so the server can de-duplicate
  if (isFormData) {
    jsonPayload.clientId = clientId;
  } else if (jsonPayload && typeof jsonPayload === 'object') {
    jsonPayload.clientId = clientId;
  }

  await db.mutationQueue.add({
    clientId,
    url,
    method: method.toUpperCase(),
    jsonPayload,
    blobs,
    isFormData,
    timestamp: Date.now(),
    status: 'pending',
    retryCount: 0,
  });

  // Let UI components know the queue changed
  window.dispatchEvent(new CustomEvent('outbox-updated'));

  return clientId;
};

/** All pending items in chronological order */
export const getPendingItems = () =>
  db.mutationQueue.where('status').equals('pending').sortBy('timestamp');

/** Mark an outbox item as complete and remove it */
export const markComplete = async (id) => {
  await db.mutationQueue.delete(id);
  window.dispatchEvent(new CustomEvent('outbox-updated'));
};

/** Mark an outbox item as failed (server error — keep for retry) */
export const markFailed = async (id, error) => {
  await db.mutationQueue.update(id, { status: 'failed', lastError: String(error) });
};

/** Remove an outbox item entirely */
export const removeItem = async (id) => {
  await db.mutationQueue.delete(id);
  window.dispatchEvent(new CustomEvent('outbox-updated'));
};

/** Current count of pending items (for the sync badge) */
export const getQueueCount = () =>
  db.mutationQueue.where('status').anyOf('pending', 'failed').count();

/** Prune items older than 24 hours to prevent stuck sync banner */
export const pruneQueue = async () => {
  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
  const oldItems = await db.mutationQueue.where('timestamp').below(oneDayAgo).toArray();
  if (oldItems.length > 0) {
    await db.mutationQueue.bulkDelete(oldItems.map(i => i.id));
    window.dispatchEvent(new CustomEvent('outbox-updated'));
  }
};

// Run cleanup on load
pruneQueue().catch(() => {});

// ─── Generic GET – Cache Helpers ───────────────────────────────────

const STORE_MAP = {
  fields: db.fields,
  crops: db.crops,
  recommendations: db.recommendations,
};

/**
 * Cache GET response data keyed by URL path.
 * storeName must be one of: 'fields' | 'crops' | 'recommendations'
 */
export const cacheData = async (storeName, cacheKey, data) => {
  const table = STORE_MAP[storeName];
  if (!table) return;
  try {
    await table.put({ cacheKey, data, cachedAt: Date.now() });
  } catch (err) {
    console.error(`[OfflineDB] cacheData(${storeName}) failed:`, err);
  }
};

/** Retrieve cached GET data. Returns the data payload or null. */
export const getCachedData = async (storeName, cacheKey) => {
  const table = STORE_MAP[storeName];
  if (!table) return null;
  try {
    const row = await table.get(cacheKey);
    return row?.data ?? null;
  } catch (err) {
    console.error(`[OfflineDB] getCachedData(${storeName}) failed:`, err);
    return null;
  }
};

// ─── Register Background Sync ──────────────────────────────────────

export const requestBackgroundSync = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await registration.sync.register('sync-data');
    }
  } catch (err) {
    console.warn('[OfflineDB] Background sync registration failed:', err);
  }
};

// ─── Export the db instance (used by useLiveQuery in React) ────────

export default db;
